// src/components/CallOutcomeModal.jsx
// Simplified Call Outcome Modal - 4 Options Only
// Philosophy: Keep it simple - lack of contact is the #1 reason people lose business

import React, { useState, useEffect } from 'react';
import { logCallOutcome } from '../api';
import { Phone, CheckCircle, PhoneOff, AlertTriangle, Calendar } from 'lucide-react';

// Simplified 4-option system - matches migration 033
const CALL_OUTCOMES = [
  {
    id: 'no_answer_voicemail',
    label: 'No Answer - Left Voicemail',
    description: 'Called but client did not answer. Left a voicemail.',
    icon: PhoneOff,
    color: 'bg-gray-100 border-gray-300 hover:bg-gray-200',
    selectedColor: 'bg-gray-200 border-gray-500 ring-2 ring-gray-300',
    iconColor: 'text-gray-600',
    scoreChange: '+5 points'
  },
  {
    id: 'contact_satisfied',
    label: 'Contact Made - Client Satisfied',
    description: 'Spoke with client. Everything is good, no issues.',
    icon: CheckCircle,
    color: 'bg-green-50 border-green-300 hover:bg-green-100',
    selectedColor: 'bg-green-100 border-green-500 ring-2 ring-green-300',
    iconColor: 'text-green-600',
    scoreChange: 'Resets to stable'
  },
  {
    id: 'contact_followup',
    label: 'Contact Made - Follow Up Needed',
    description: 'Spoke with client but need to follow up on something.',
    icon: Calendar,
    color: 'bg-yellow-50 border-yellow-300 hover:bg-yellow-100',
    selectedColor: 'bg-yellow-100 border-yellow-500 ring-2 ring-yellow-300',
    iconColor: 'text-yellow-600',
    scoreChange: '-5 points'
  },
  {
    id: 'bad_phone_number',
    label: 'Bad Phone Number',
    description: 'Phone number is disconnected or wrong number.',
    icon: AlertTriangle,
    color: 'bg-red-50 border-red-300 hover:bg-red-100',
    selectedColor: 'bg-red-100 border-red-500 ring-2 ring-red-300',
    iconColor: 'text-red-600',
    scoreChange: '+15 points'
  }
];

export default function CallOutcomeModal({ isOpen, onClose, client, onSuccess }) {
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [notes, setNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOutcome(null);
      setNotes('');
      setFollowUpDate('');
      setError(null);
    }
  }, [isOpen]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedOutcome) {
      setError('Please select a call outcome');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await logCallOutcome({
        clientId: client.id,
        outcomeCode: selectedOutcome.id,
        notes,
        callbackDate: followUpDate || null
      });

      // Dispatch event to refresh risk score card
      window.dispatchEvent(new CustomEvent('risk-score-updated', { detail: { clientId: client.id } }));

      // Call onSuccess callback (this will refresh client data)
      if (onSuccess) {
        await onSuccess();
      }

      // Close the modal
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to log call outcome');
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  const needsFollowUp = selectedOutcome?.id === 'contact_followup';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden transform transition-all">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#172A3A] to-slate-800 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#FFB800] flex items-center justify-center">
                <Phone className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Log Call Outcome</h2>
                {client && (
                  <p className="text-slate-300 text-sm mt-0.5">
                    {client.first_name || client.firstName} {client.last_name || client.lastName}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-5">
              {/* Outcome Options - 4 big buttons */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  What was the outcome of the call?
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {CALL_OUTCOMES.map(outcome => {
                    const Icon = outcome.icon;
                    const isSelected = selectedOutcome?.id === outcome.id;
                    return (
                      <button
                        key={outcome.id}
                        type="button"
                        onClick={() => setSelectedOutcome(outcome)}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected ? outcome.selectedColor : outcome.color
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-white shadow-md' : 'bg-white/60'
                        }`}>
                          <Icon size={24} className={outcome.iconColor} />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-slate-900">{outcome.label}</div>
                          <div className="text-sm text-slate-600 mt-0.5">{outcome.description}</div>
                        </div>
                        <div className={`text-xs font-mono px-2 py-1 rounded ${
                          outcome.scoreChange.includes('+') ? 'bg-red-100 text-red-700' :
                          outcome.scoreChange.includes('-') ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {outcome.scoreChange}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Follow-up Date - only shown for Follow Up Needed */}
              {needsFollowUp && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-yellow-800 mb-2">
                    When should you follow up?
                  </label>
                  <input
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full rounded-lg border-yellow-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                    required={needsFollowUp}
                  />
                  <p className="text-xs text-yellow-700 mt-2">
                    A task will be automatically created for this follow-up.
                  </p>
                </div>
              )}

              {/* Notes - prominently displayed */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Call Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm resize-none"
                  placeholder="Add details about the conversation, client concerns, action items, etc..."
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!selectedOutcome || submitting || (needsFollowUp && !followUpDate)}
                className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#172A3A] to-slate-700 rounded-xl hover:from-slate-800 hover:to-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </span>
                ) : 'Save Outcome'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
